import React, { useEffect, useState } from 'react';
import Button from './Button';
import { derivedRouteName } from './routeName';
import './AddRouteModal.css';

// A ROUTE ON A TEMPLATE: WHAT IT CARRIES, AND WHAT IT IS CALLED.
//
// It used to ask for a credential and an x-hotelid as well. Neither is a property of the TEMPLATE —
// they are properties of a PLACE the template runs — and asking for them here pinned the whole design
// to one hotel, so "the same pipeline for the next property" meant drawing it again. They belong to an
// implementation (V508), which fills one slot per route with that property's credentials.
//
// WHAT IS LEFT IS WHAT IS ACTUALLY REUSABLE: the Universal API modules a route carries, the KIND of
// backend it lands on, and its name. All three are true of the shape wherever it runs.
//
// THE KIND OF BACKEND IS A DESIGN DECISION (V510), even though WHICH ONE is not. "Reservations, to
// Opera Cloud via OHIP" and "reservations, to RMS Cloud" are different designs — different payloads,
// different custom code, different failures — so the template is where that is settled. The
// implementation then chooses among that property's bindings OF THAT SYSTEM, which is a question with
// a short answer, instead of among everything it has.
//
// THE MODULE LIST IS NO LONGER FILTERED BY GRANTS. Grants belong to a credential and there is no
// credential here, so every module the source system publishes is offered; whether a given property's
// credential may ask for it is settled when that implementation is filled in. The alternative is a
// template permanently limited by whichever credential happened to be selected while drawing it.
// `vendor` (V704): the route starts at a vendor's board — it calls the Universal API itself, so it names no modules of its
// own and carries what the backend chosen below serves.
const AddRouteModal = ({ isOpen, onClose, modules, existing, route, sourceAdapter, systems, onAdd, vendor = null }) => {
    const [picked, setPicked] = useState(new Set());
    // WHICH KIND OF BACKEND. Optional — a route may deliberately be left open, and an old one has no
    // answer — so it is offered rather than demanded.
    const [targetId, setTargetId] = useState('');
    // WHAT THE ROUTE IS CALLED (V491). Left empty it takes the derived name shown as the placeholder, so
    // a route always has a name without anybody having to invent one.
    const [name, setName] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setPicked(new Set((route?.modules || []).map(m => String(m.uapi_route_id))));
        setName(route?.name || '');
        setTargetId(route?.target_marketplace_id ? String(route.target_marketplace_id) : '');
    }, [isOpen, route]);

    if (!isOpen) return null;

    // ALREADY CARRIED BY ANOTHER ROUTE FROM THIS PROXY. Not forbidden — two routes may legitimately
    // carry the same module with different custom code on each — so it is a note beside the row rather
    // than a disabled checkbox. Saying nothing is what lets somebody build the same route twice without
    // noticing.
    //
    // MATCHED ON THE PROXY, not on a destination: a template has no destination until it is
    // implemented, and the question here is whether this shape already carries that module.
    const already = (m) => (existing || []).some(r =>
        r !== route && (r.modules || []).some(x => String(x.uapi_route_id) === String(m.uapi_route_id)));

    // WHAT THE UNIVERSAL API CAN CALL. `uapi_enabled` is the flag that answers it, read off the
    // marketplace record by id — never the system's name or its proxy's, which is how an MCP source
    // once opened the OXI test suite.
    const backends = (systems || []).filter(s => s.uapi_enabled === 'Y');
    const chosenBackend = backends.find(s => String(s.id) === String(targetId)) || null;

    // THE MODULES ON OFFER: the source system's, AND THOSE OF THE BACKEND THE ROUTE IS DESIGNED FOR. A module is a root
    // the Universal API serves, and the system that serves it is the backend — so a backend may serve a module its
    // sources do not name, and a route to it must be able to carry that. Backend-only modules are marked with the system
    // that serves them. (Every system speaks the platform's one module language — /ari, /rsv, /content… — V657.)
    const offered = (() => {
        const out = (modules || []).map(m => ({ ...m, servedBy: null }));
        const have = new Set(out.map(m => String(m.uapi_route_id)));
        for (const m of (chosenBackend && chosenBackend.modules) || []) {
            if (have.has(String(m.uapi_route_id))) continue;
            have.add(String(m.uapi_route_id));
            out.push({ ...m, servedBy: chosenBackend.name });
        }
        return out;
    })();
    const chosenModules = offered.filter(m => picked.has(String(m.uapi_route_id)));

    // The name this route would have if the box is left empty — recomputed as modules are ticked, so
    // the placeholder always describes what is actually on screen. No backend is named: a template does
    // not know where it lands, and derivedRouteName leaves that half off rather than inventing it.
    const suggestion = derivedRouteName(
        { modules: chosenModules, target_system_name: chosenBackend ? chosenBackend.name : null },
        sourceAdapter);

    const toggle = (id) => setPicked(prev => {
        const next = new Set(prev);
        const key = String(id);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-md acm-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{route ? 'Edit route' : 'Add a route'}</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <p className="acm-hint acm-lede">
                        A route is what {vendor ? `${vendor} calls` : 'this proxy carries'} through the Universal API. The credentials it
                        travels with, and the property it lands on, belong to an <strong>implementation</strong>
                        {' '}— this is the shape they fill in.
                    </p>

                    <div className="acm-field">
                        <span className="acm-label"><span className="acm-step">1</span> Modules</span>
                        {offered.length === 0 ? (
                            <p className="acm-none">
                                {vendor
                                    ? 'Choose the backend below — a vendor calls the modules its target serves (/mq for a queue, /rsv for OPERA, …).'
                                    : 'This system names no modules on its marketplace record, so there is nothing to '
                                      + 'route yet. Add them on the Modules tab of its marketplace record.'}
                            </p>
                        ) : (
                            <>
                                <ul className="acm-module-list">
                                    {offered.map(m => {
                                        const have = already(m);
                                        return (
                                            <li key={m.id}>
                                                <label className="acm-module">
                                                    <input
                                                        type="checkbox"
                                                        checked={picked.has(String(m.uapi_route_id))}
                                                        onChange={() => toggle(m.uapi_route_id)}
                                                    />
                                                    <span className="acm-module-name">{m.name}</span>
                                                    <span className="acm-module-path">{m.uapi_route_path}</span>
                                                    {m.servedBy && <span className="acm-module-had">served by {m.servedBy}</span>}
                                                    {have && <span className="acm-module-had">already on another route</span>}
                                                </label>
                                            </li>
                                        );
                                    })}
                                </ul>
                                <p className="acm-hint">
                                    Everything the Universal API publishes under a ticked module travels this
                                    route, including operations added to it later. Choose the backend system below
                                    to add the modules it serves.
                                </p>
                            </>
                        )}
                    </div>

                    {/* THE KIND OF BACKEND. Between the modules and the name because the name is derived
                        from both, and because "what does it carry, and where does that go" is the order
                        somebody thinks in. */}
                    <div className="acm-field">
                        <span className="acm-label"><span className="acm-step">2</span> Backend system</span>
                        <select
                            className="acm-input"
                            value={targetId}
                            onChange={(e) => setTargetId(e.target.value)}
                        >
                            <option value="">Any — decided by the implementation</option>
                            {backends.map(s => (
                                <option key={s.id} value={String(s.id)}>{s.name}</option>
                            ))}
                        </select>
                        <p className="acm-hint">
                            {chosenBackend
                                ? `Every implementation of this template picks one of its property's `
                                  + `${chosenBackend.name} bindings for this route. A binding of any other `
                                  + 'system is refused.'
                                : 'Left open, an implementation may point this route at any backend the '
                                  + 'property is bound to. Name one where the design only makes sense '
                                  + 'against that system.'}
                        </p>
                    </div>

                    {/* WHAT IT IS CALLED — after the modules, because the suggestion is written from them.
                        Empty means the suggestion, which is what the canvas, the custom code and the logs
                        will then show. */}
                    <div className="acm-field">
                        <span className="acm-label"><span className="acm-step">3</span> Name</span>
                        <input
                            type="text"
                            className="acm-input"
                            maxLength={120}
                            value={name}
                            placeholder={suggestion}
                            onChange={(e) => setName(e.target.value)}
                        />
                        <p className="acm-hint">
                            How this route is named on the canvas, in its custom code and in the logs.
                            Leave it empty to use <code>{suggestion}</code>.
                        </p>
                    </div>
                </div>

                <div className="modal-footer acm-footer">
                    <Button
                        variant="primary"
                        disabled={picked.size === 0}
                        onClick={() => {
                            onAdd({
                                moduleIds: [...picked].map(Number),
                                targetMarketplaceId: targetId ? Number(targetId) : null,
                                name: name.trim() || suggestion,
                            });
                            onClose();
                        }}
                    >
                        {route
                            ? 'Save route'
                            : picked.size > 0
                                ? `Add route with ${picked.size} module${picked.size === 1 ? '' : 's'}`
                                : 'Add route'}
                    </Button>
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                </div>
            </div>
        </div>
    );
};

export default AddRouteModal;
